import React, { useEffect, useRef, useState } from "react";
import type { BoardProps } from "../pages/room";
import { t } from "../i18n";
import { Button } from "../ui";
import { useReplay } from "./pool";

const S = 740;
const COIN = 16;
const STRIKER = 21;
const owner = (id: number) => (id === 1 ? -1 : id <= 10 ? 0 : 1);

export function CarromBoard({ view, seat, names, myTurn, move, events }: BoardProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const frame = useReplay(events);
  const [sx, setSx] = useState(370);
  const [angle, setAngle] = useState(seat === 1 ? Math.PI / 2 : -Math.PI / 2);
  const [power, setPower] = useState(0.55);
  const animating = !!frame;
  const baseY = view.baseline.y;
  // flip the board for the top player so their baseline is at the bottom
  const flip = seat === 1;
  const coins: { id: number; x: number; y: number }[] = frame ? frame.map(([id, x, y]) => ({ id, x, y })) : view.coins.filter((c: any) => !c.pocketed);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ctx = c.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const cw = c.clientWidth;
    c.width = c.height = cw * dpr;
    const s = (cw * dpr) / (S + 40);
    ctx.setTransform(s, 0, 0, s, 20 * s, 20 * s);
    if (flip) {
      ctx.translate(S, S);
      ctx.rotate(Math.PI);
    }
    ctx.fillStyle = "#5a3418";
    ctx.fillRect(-20, -20, S + 40, S + 40);
    ctx.fillStyle = "#EBC98F";
    ctx.fillRect(0, 0, S, S);
    ctx.fillStyle = "#16121f";
    for (const [x, y] of [[22, 22], [718, 22], [22, 718], [718, 718]]) {
      ctx.beginPath();
      ctx.arc(x, y, 28, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = "#8a4a1e";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(370, 370, 80, 0, Math.PI * 2);
    ctx.stroke();
    for (const y of [100, 640]) {
      ctx.strokeRect(160, y - 18, 420, 36);
    }
    for (const c of coins) {
      if (c.id === 0) continue;
      ctx.beginPath();
      ctx.arc(c.x, c.y, COIN, 0, Math.PI * 2);
      ctx.fillStyle = c.id === 1 ? "#D93A2B" : owner(c.id) === 0 ? "#FFF4E0" : "#2A2033";
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,.35)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    const striker = frame?.find(([id]) => id === 0);
    const stx = striker ? striker[1] : sx;
    const sty = striker ? striker[2] : baseY;
    if (striker || (myTurn && !animating)) {
      ctx.beginPath();
      ctx.arc(stx, sty, STRIKER, 0, Math.PI * 2);
      ctx.fillStyle = "#F7B32B";
      ctx.fill();
      ctx.strokeStyle = "#8a5a00";
      ctx.stroke();
    }
    if (myTurn && !animating) {
      ctx.setLineDash([8, 8]);
      ctx.strokeStyle = "rgba(26,18,64,.6)";
      ctx.beginPath();
      ctx.moveTo(sx, baseY);
      ctx.lineTo(sx + Math.cos(angle) * (120 + power * 300), baseY + Math.sin(angle) * (120 + power * 300));
      ctx.stroke();
      ctx.setLineDash([]);
    }
  });

  const onPointer = (e: React.PointerEvent) => {
    if (!myTurn || animating || !(e.type === "pointerdown" || e.buttons)) return;
    const r = canvas.current!.getBoundingClientRect();
    const sc = r.width / (S + 40);
    let x = (e.clientX - r.left) / sc - 20;
    let y = (e.clientY - r.top) / sc - 20;
    if (flip) [x, y] = [S - x, S - y];
    setAngle(Math.atan2(y - baseY, x - sx));
  };
  const mine = view.coins.filter((c: any) => !c.pocketed && owner(c.id) === seat).length;
  const theirs = view.coins.filter((c: any) => !c.pocketed && owner(c.id) === 1 - seat).length;

  return (
    <div className="carrom">
      <div className="pool-status">
        <span className={view.turn === seat ? "is-turn" : ""}>{t.you}: {9 - mine}/9</span>
        <span className={view.turn !== seat ? "is-turn" : ""}>{names[1 - seat]}: {9 - theirs}/9</span>
        {view.queen.coveredBy !== null && <span>{t.queen}: {names[view.queen.coveredBy]}</span>}
      </div>
      <canvas ref={canvas} className="carrom-board" onPointerDown={onPointer} onPointerMove={onPointer} style={{ touchAction: "none" }} aria-label={t.game.carrom} />
      <div className="board-controls">
        {myTurn ? (
          <>
            <span className="hint">{t.strikerHint}</span>
            <input type="range" min={view.baseline.x[0]} max={view.baseline.x[1]} value={sx} onChange={(e) => setSx(Number(e.target.value))} aria-label="striker" dir={flip ? "rtl" : "ltr"} />
            <label className="power">
              {t.power}
              <input type="range" min="0.05" max="1" step="0.01" value={power} onChange={(e) => setPower(Number(e.target.value))} />
            </label>
            <Button disabled={animating} onClick={() => move({ type: "shoot", x: sx, angle, power })}>{t.shoot}</Button>
          </>
        ) : (
          <span className="hint">{t.turnOf(names[view.turn] ?? "")}</span>
        )}
      </div>
    </div>
  );
}
